/**
 * A date-range filter selection.
 *
 * Mirrors the shape of the SDK's `DateRangeInput` for list-search
 * commands (`SearchTasksCommandInput.dateRange`,
 * `SearchAuditsCommandInput.dateRange`), but kept as a plain client
 * shape so the persistence layer can serialize/deserialize dates as
 * strings instead of `Date` instances. Command inputs coerce these
 * strings to `Date` at the query boundary.
 *
 * - `type` is a `DateRangeType` value (`PAST_WEEK`, `PAST_MONTH`,
 *   `PAST_YEAR`, `CUSTOM`) or empty when unset.
 * - `startDate` / `endDate` are only set when `type === 'CUSTOM'`.
 */
export interface FilterDateRange {
  type: string;
  startDate?: string;
  endDate?: string;
}

/** Radio value that opens the custom date-range picker. */
export const CUSTOM_DATE_RANGE_TYPE = 'CUSTOM';

/**
 * Build the dateRange filter object from the radio selection + optional
 * custom dates. Preset ranges never carry dates; a partial custom range
 * (only start or only end) is preserved as-is so the picker can restore
 * the in-flight selection.
 */
export function buildDateRangeSelection(
  type: string,
  customStart: string | undefined,
  customEnd: string | undefined,
): FilterDateRange | null {
  if (!type) return null;
  if (type === CUSTOM_DATE_RANGE_TYPE) {
    return {
      type,
      ...(customStart ? { startDate: customStart } : {}),
      ...(customEnd ? { endDate: customEnd } : {}),
    };
  }
  return { type };
}

/**
 * Calendar-date availability for the custom date range picker.
 *
 * - When the Custom range type is NOT selected, every date is disabled —
 *   the picker is visible but inert until the user picks "Custom".
 * - When Custom IS selected, all dates are selectable — users need
 *   visibility into upcoming audits/tasks within the current week or
 *   month to plan accordingly.
 */
export function isFilterDateDisabled(isCustomSelected: boolean): boolean {
  if (!isCustomSelected) return true;
  return false;
}

/**
 * Compact "Jan 5 – Feb 3" title for the date-range filter chip when a
 * complete custom range is selected. Returns null for a partial/empty
 * range so the caller can fall back to the default chip title.
 */
export function formatCustomRangeTitle(
  startDate: string | undefined,
  endDate: string | undefined,
): string | null {
  if (!startDate || !endDate) return null;
  const format = (dateStr: string): string =>
    new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  return `${format(startDate)} – ${format(endDate)}`;
}
